import React from 'react';
import { useParams } from 'react-router-dom';

import CardHeader from '../../common/Labels/CardHeader';
import HeadingLabel from '../../common/Labels/HeadingLabel';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import PipelineIcon from '../../common/icons/PipelineIcon';
import StickyBar from '../../common/StickyBar/StickyBar';
import SubHeadingLabel from '../../common/Labels/SubHeadingLabel';
import VersionIcon from '../../common/icons/VersionIcon';

import ContentTags from '../../ContentTags/ContentTags';
import LogsPanel from '../../LogsPanel/LogsPanel';
import PipelineActivityLog from '../PipelineActivityLog/PipelineActivityLog';
import PipelineStatusBadge from '../PipelineStatusBadge/PipelineStatusBadge';
import TimeRangePicker from '../../TimeRangePicker/TimeRangePicker';

import usePipelineData, { PipelineDataProvider } from '../PipelineDataContext';
import { PipelineCommentsProvider } from '../../contexts/CommentData/CommentData';

import {
  TimeRangeProvider,
  usePreferredTimeRange,
} from '../../contexts/TimeRangeContext/TimeRangeContext';
import { useRedirectOnWorkspaceChange } from '../../../utils/hooks/use_redirect_on_value_change';

import AnomalyCountChart from '../../charts/prefabs/AnomalyCountChart';
import InferenceLatencyChart from '../../charts/prefabs/InferenceLatencyChart';
import InferenceRateChart from '../../charts/prefabs/InferenceRateChart';
import RequestPerSecondChart from '../../charts/prefabs/RequestPerSecondChart';
import ReplicaCountChart from '../../charts/prefabs/ReplicaCountChart';
import SidekickReplicaCountChart from '../../charts/prefabs/SidekickReplicaCountChart';
import { usePipelineExternalUrl } from '../../../utils/hooks/use_pipeline_access_points';

export const PipelineDetailSingleChart = ({
  pipeline,
  deploymentPipelineVersions,
  error,
  handleAddTag,
  handleDeleteTag,
}) => {
  const routeParams = useParams();
  const onlyShowChartType = routeParams?.chartType;

  const {
    startTimestamp,
    endTimestamp,
    step,
    selectedTimeRange,
    onTimeRangeChange,
  } = usePreferredTimeRange();

  const range = endTimestamp - startTimestamp;
  const labelFormatString = range > 24 * 60 * 60
    ? 'LLL d k:mm:ss'
    : 'k:mm:ss';

  const workspaceId = routeParams?.workspaceId;
  const pipelineId = routeParams?.id;
  const { externalUrl } = usePipelineExternalUrl(workspaceId, pipelineId);
  const backupDeploymentID = `${deploymentPipelineVersions?.deployment?.deploy_id}-${deploymentPipelineVersions?.deployment?.id}`;

  const urlParts = externalUrl?.length ? externalUrl.split('/') : [];
  const deploymentID = urlParts?.length ? urlParts[urlParts.length - 2] : backupDeploymentID;

  return (
    <div className="w-full mb-16">
      <div className="bg-brand border-t-2 border-border-brand text-white flex justify-center items-center">
        <div className="ml-2 px-2 py-8 xl:px-0 w-full flex-grow max-w-screen-xl flex flex-col items-start">
          <div className="flex items-center">
            <Icon icon={PipelineIcon} size={80} useTailwindSizing />
            <HeadingLabel className="ml-2 mt-1" size="4xl">
              {routeParams?.id}
            </HeadingLabel>
          </div>
          <div className="ml-12 mb-2 flex items-center">
            <Icon icon={VersionIcon} size={32} useTailwindSizing />
            <SubHeadingLabel className="ml-2" size="2xl">
              {routeParams?.version}
            </SubHeadingLabel>
          </div>
          {
            !pipeline ? (
              error ? (
                <SubHeadingLabel className="ml-2 mt-2" size="2xl">
                      Something went wrong loading this Pipeline
                </SubHeadingLabel>
              ) : (
                <div className="ml-2 mt-2 flex flex-row">
                  <SubHeadingLabel size="2xl">
                        Loading Pipeline Data
                  </SubHeadingLabel>
                  <LoadingSpinner className="ml-2" animationKey="pipeline-detail-loading-spinner" />
                </div>
              )
            ) : (
              <div className="ml-2 mt-2 flex items-center">
                <PipelineStatusBadge status={pipeline?.status} />
                <div className="ml-2 w-4/5 text-brand">
                  <ContentTags
                    tags={pipeline.pipelineTags.map(({ tag }) => tag.tag)}
                    onAddTag={handleAddTag}
                    onDeleteTag={handleDeleteTag}
                  />
                </div>
              </div>
            )
          }
        </div>
      </div>
      {!error && (
        <div className="mt-4 flex justify-center items-center">
          <div className="w-full max-w-screen-xl px-2 xl:px-0">
            <StickyBar id="pipeline-detail-control-row">
              <CardHeader className="mb-2">Viewing Pipeline Data for {routeParams.id}</CardHeader>
              <div className="flex flex-row">
                <TimeRangePicker selectedRange={selectedTimeRange} onChange={onTimeRangeChange} />
              </div>
            </StickyBar>
            <div className="grid grid-cols-1 gap-x-6 gap-y-3 mt-4 grid-rows-enlarged-card">
              {
                (onlyShowChartType === 'anomaly-count') && (
                  <AnomalyCountChart
                    chartID="anomaly-count"
                    pipelineID={pipeline?.pipelineID}
                    pipelineVersion={routeParams?.version}
                    deploymentID={deploymentID}
                    labelFormatString={labelFormatString}
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    step={step}
                    isEnlarged={onlyShowChartType === 'anomaly-count'}
                  />
                )
              }
              {
                (onlyShowChartType === 'requests-per-second') && (
                  <RequestPerSecondChart
                    chartID="requests-per-second"
                    pipelineID={pipeline?.pipelineID}
                    pipelineVersion={routeParams?.version}
                    deploymentID={deploymentID}
                    labelFormatString={labelFormatString}
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    step={step}
                    isEnlarged={onlyShowChartType === 'requests-per-second'}
                  />
                )
              }
              {
                (onlyShowChartType === 'inference-rate') && (
                  <InferenceRateChart
                    chartID="inference-rate"
                    pipelineID={pipeline?.pipelineID}
                    pipelineVersion={routeParams?.version}
                    deploymentID={deploymentID}
                    labelFormatString={labelFormatString}
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    step={step}
                    isEnlarged={onlyShowChartType === 'inference-rate'}
                  />
                )
              }
              {
                (onlyShowChartType === 'inference-latency') && (
                  <InferenceLatencyChart
                    chartID="inference-latency"
                    pipelineID={pipeline?.pipelineID}
                    pipelineVersion={routeParams?.version}
                    deploymentID={deploymentID}
                    labelFormatString={labelFormatString}
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    step={step}
                    isEnlarged={onlyShowChartType === 'inference-latency'}
                  />
                )
              }
              {
                (onlyShowChartType === 'replica-count') && (
                  <ReplicaCountChart
                    chartID="replica-count"
                    pipelineID={pipeline?.pipelineID}
                    pipelineVersion={routeParams?.version}
                    deploymentID={deploymentID}
                    labelFormatString={labelFormatString}
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    step={step}
                    isEnlarged={onlyShowChartType === 'replica-count'}
                  />
                )
              }
              {
                (onlyShowChartType === 'sidekick-replica-count') && (
                  <SidekickReplicaCountChart
                    chartID="sidekick-replica-count"
                    pipelineID={pipeline?.pipelineID}
                    pipelineVersion={routeParams?.version}
                    deploymentID={deploymentID}
                    labelFormatString={labelFormatString}
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    step={step}
                    isEnlarged={onlyShowChartType === 'sidekick-replica-count'}
                  />
                )
              }
            </div>
            <div
              className="grid grid-cols-1 grid-rows-single-card gap-x-6 gap-y-3 mt-8"
            >
              <LogsPanel
                pipelineID={routeParams.id}
                isAnomalyDetectionEnabled={onlyShowChartType === 'anomaly-count'}
              />
            </div>
            {!onlyShowChartType && (
              <PipelineCommentsProvider pipeline={pipeline}>
                <PipelineActivityLog pipeline={pipeline} />
              </PipelineCommentsProvider>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ContextualPipelineDetailPage = (props) => {
  const {
    pipeline,
    pipelineSteps,
    latestPipelineDeployment,
    deploymentPipelineVersions,
    loading,
    error,
    pipelineHistory,
    pipelineHistoryError,
    pipelineHistoryLoading,
    isAnomalyDetectionEnabled,
    addPipelineTag,
    deletePipelineTag,
  } = usePipelineData();

  useRedirectOnWorkspaceChange('/pipelines')

  return (
    <PipelineDetailSingleChart
      pipeline={pipeline}
      pipelineSteps={pipelineSteps}
      latestPipelineDeployment={latestPipelineDeployment}
      deploymentPipelineVersions={deploymentPipelineVersions}
      loading={loading}
      error={error}
      pipelineHistory={pipelineHistory}
      pipelineHistoryError={pipelineHistoryError}
      pipelineHistoryLoading={pipelineHistoryLoading}
      isAnomalyDetectionEnabled={isAnomalyDetectionEnabled}
      handleAddTag={addPipelineTag}
      handleDeleteTag={deletePipelineTag}
      {...props}
    />
  )
}

const WrappedPipelineDetailPage = props => (
  <PipelineDataProvider>
    <TimeRangeProvider>
      <ContextualPipelineDetailPage {...props} />
    </TimeRangeProvider>
  </PipelineDataProvider>
)

export default WrappedPipelineDetailPage;
